// @ts-nocheck
// Note: react-konva types have conflicts, using nocheck
import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Stage, Layer, Circle, Text, Group, Rect } from 'react-konva';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Konva from 'konva';
import { MissionMapData, MissionDevicePosition } from '../../types/gameDetail';

const CANVAS_WIDTH = 1600;
const CANVAS_HEIGHT = 1000;

const ViewerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #0a0a15;
  position: relative;
  border: 1px solid #00d4ff;
  border-radius: 12px;
  overflow: hidden;
`;

const CanvasContainer = styled.div`
  flex: 1;
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: #0a0a15;
`;

const StageWrapper = styled.div`
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 10, 21, 0.8);
  color: #00d4ff;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
`;

const FallbackImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const FallbackImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: fill;
`;

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px 16px;
  background: var(--legacy-card-bg);
  border-top: 1px solid #00d4ff;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => props.$active ? 'rgba(0, 212, 255, 0.2)' : 'rgba(0, 212, 255, 0.1)'};
  border: 1px solid ${props => props.$active ? '#00d4ff' : 'rgba(0, 212, 255, 0.5)'};
  border-radius: 4px;
  color: ${props => props.$active ? '#00d4ff' : '#8bb8d8'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Orbitron', sans-serif;

  &:hover {
    background: rgba(0, 212, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 16px;
  border-left: 1px solid rgba(0, 212, 255, 0.3);
  margin-left: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #8bb8d8;
  font-family: 'Orbitron', sans-serif;
`;

const LegendIcon = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  flex-direction: column;
`;

const FullscreenHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: #0a0a15;
  border-bottom: 1px solid #00d4ff;
`;

const FullscreenTitle = styled.h3`
  margin: 0;
  color: #00d4ff;
  font-family: 'Orbitron', sans-serif;
  font-size: 18px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid #ff4444;
  border-radius: 4px;
  color: #ff4444;
  cursor: pointer;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  transition: all 0.2s;
  z-index: 10000;

  &:hover {
    background: rgba(255, 0, 0, 0.3);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FullscreenContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
`;

const FullscreenMapWrapper = styled.div`
  position: relative;
  border-radius: 8px;
  box-shadow: 0 0 40px rgba(0, 212, 255, 0.3);
  overflow: hidden;
`;

const MapHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  background: var(--legacy-card-bg);
  border-bottom: 1px solid #00d4ff;
  flex-wrap: wrap;
`;

const HeaderItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #8bb8d8;
  font-family: 'Orbitron', sans-serif;
`;

const HeaderLabel = styled.span`
  color: rgba(139, 184, 216, 0.7);
  font-size: 13px;
`;

const HeaderValue = styled.span`
  color: #00d4ff;
  font-weight: 500;
`;

const FieldNameLink = styled(Link)`
  color: #4CAF50;
  text-decoration: none;
  transition: all 0.2s;
  font-weight: 600;
  font-size: 15px;

  &:hover {
    color: #81C784;
    text-decoration: underline;
  }
`;

const MapNameLink = styled(Link)`
  color: #FFD700;
  text-decoration: none;
  transition: all 0.2s;
  font-weight: 600;
  font-size: 15px;

  &:hover {
    color: #FFEB3B;
    text-decoration: underline;
  }
`;

const TemplateValue = styled.span`
  color: #FF9800;
  font-weight: 600;
  font-size: 15px;
`;

const HeaderDivider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(0, 212, 255, 0.4);
`;

interface MissionMapViewerProps {
  mapData: MissionMapData;
  devices?: MissionDevicePosition[];
  showDevices?: boolean;
  showLabels?: boolean;
  height?: string;
  // Header info
  fieldId?: string;
  fieldName?: string;
  operationTemplateName?: string;
}

const getDeviceColor = (deviceType: string): string => {
  switch (deviceType?.toLowerCase()) {
    case 'bomb':
    case 'explosive':
      return '#FF5722';
    case 'flag':
    case 'capture':
      return '#4CAF50';
    case 'terminal':
    case 'control':
      return '#2196F3';
    case 'sensor':
    case 'detector':
      return '#9C27B0';
    case 'main':
      return '#FFD700';
    default:
      return '#00d4ff';
  }
};

const MissionMapViewer: React.FC<MissionMapViewerProps> = ({
  mapData,
  devices = [],
  showDevices = true,
  showLabels = true,
  height = '400px',
  fieldId,
  fieldName,
  operationTemplateName,
}) => {
  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [stageSize, setStageSize] = useState({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
  const [showDeviceIcons, setShowDeviceIcons] = useState(showDevices);
  const [showDeviceLabels, setShowDeviceLabels] = useState(showLabels);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenSize, setFullscreenSize] = useState({ width: 0, height: 0 });

  // Получаем URL изображения
  const imageUrl = mapData.exportedImageUrl || mapData.schemaImageUrl;

  // Сброс состояния загрузки при изменении URL
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [imageUrl]);

  // Sync showDevices prop with internal state
  useEffect(() => {
    setShowDeviceIcons(showDevices);
  }, [showDevices]);

  // Расчёт масштаба canvas под контейнер
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;

        // Skip if container has no size yet
        if (containerWidth === 0 || containerHeight === 0) return;

        const scaleX = containerWidth / CANVAS_WIDTH;
        const scaleY = containerHeight / CANVAS_HEIGHT;
        const scale = Math.min(scaleX, scaleY, 1);

        setStageSize({
          width: CANVAS_WIDTH * scale,
          height: CANVAS_HEIGHT * scale,
        });
      }
    };

    // Use ResizeObserver for better detection
    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Initial calculation with small delay to ensure DOM is ready
    setTimeout(updateSize, 0);

    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      resizeObserver.disconnect();
    };
  }, []);

  // Render device (same style as MapSchemaEditor)
  const renderDevice = (device: MissionDevicePosition, index: number, customScale?: number) => {
    const scale = customScale || stageSize.width / CANVAS_WIDTH;
    const deviceScale = device.scale || 1;
    const baseSize = 48; // Same as MapSchemaEditor
    const size = baseSize * deviceScale * scale;
    const color = getDeviceColor(device.deviceType);

    return (
      <Group
        key={device.entryId}
        x={device.positionX * scale}
        y={device.positionY * scale}
      >
        {/* Device circle */}
        <Circle
          radius={size / 2}
          fill={color}
          stroke="#000"
          strokeWidth={2}
        />

        {/* Device number */}
        <Text
          text={(index + 1).toString()}
          fontSize={16 * scale}
          fill="#fff"
          fontStyle="bold"
          align="center"
          verticalAlign="middle"
          offsetX={8}
          offsetY={8}
          width={16}
        />

        {/* Device label background */}
        {showDeviceLabels && (
          <Rect
            x={-60 * scale}
            y={size / 2 + 4}
            width={120 * scale}
            height={20 * scale}
            fill="rgba(0, 0, 0, 0.7)"
            cornerRadius={3}
          />
        )}

        {/* Device label - show deviceType */}
        {showDeviceLabels && (
          <Text
            text={device.deviceType}
            fontSize={14 * scale}
            fill="#fff"
            x={-60 * scale}
            y={size / 2 + 6}
            width={120 * scale}
            align="center"
            fontStyle="bold"
          />
        )}
      </Group>
    );
  };

  const openFullscreen = () => {
    // Вычисляем размер для fullscreen с сохранением пропорций
    const maxWidth = window.innerWidth - 48;
    const maxHeight = window.innerHeight - 150;
    const scaleX = maxWidth / CANVAS_WIDTH;
    const scaleY = maxHeight / CANVAS_HEIGHT;
    const fsScale = Math.min(scaleX, scaleY);

    setFullscreenSize({
      width: CANVAS_WIDTH * fsScale,
      height: CANVAS_HEIGHT * fsScale,
    });
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    document.body.style.overflow = '';
  };

  // Закрытие по Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <ViewerContainer style={{ height }}>
      {/* Header с информацией о карте */}
      <MapHeader>
        {fieldName && (
          <>
            <HeaderItem>
              <HeaderLabel>Field:</HeaderLabel>
              {fieldId ? (
                <FieldNameLink to={`/fields/${fieldId}`}>{fieldName}</FieldNameLink>
              ) : (
                <HeaderValue>{fieldName}</HeaderValue>
              )}
            </HeaderItem>
            <HeaderDivider />
          </>
        )}
        <HeaderItem>
          <HeaderLabel>Map:</HeaderLabel>
          {fieldId ? (
            <MapNameLink to={`/fields/${fieldId}/maps/${mapData.id}`}>{mapData.name}</MapNameLink>
          ) : (
            <MapNameLink to={`/maps/${mapData.id}`}>{mapData.name}</MapNameLink>
          )}
        </HeaderItem>
        {operationTemplateName && (
          <>
            <HeaderDivider />
            <HeaderItem>
              <HeaderLabel>Template:</HeaderLabel>
              <TemplateValue>{operationTemplateName}</TemplateValue>
            </HeaderItem>
          </>
        )}
      </MapHeader>

      <CanvasContainer ref={containerRef}>
        <StageWrapper style={{ width: stageSize.width, height: stageSize.height }}>
          {/* Фоновое изображение карты */}
          {imageUrl && (
            <FallbackImageContainer>
              {imageError && (
                <LoadingOverlay>Failed to load map image</LoadingOverlay>
              )}
              <FallbackImage
                src={imageUrl}
                alt="Mission Map"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </FallbackImageContainer>
          )}

          {/* Canvas для устройств поверх изображения */}
          <Stage
            ref={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            style={{ position: 'absolute', top: 0, left: 0 }}
          >
            {showDeviceIcons && devices.length > 0 && (
              <Layer>
                {devices.map((device, index) => renderDevice(device, index))}
              </Layer>
            )}
          </Stage>
        </StageWrapper>
      </CanvasContainer>

      <ControlsBar>
        {devices.length > 0 && (
          <>
            <ToggleButton
              $active={showDeviceIcons}
              onClick={() => setShowDeviceIcons(prev => !prev)}
              title="Show/Hide Devices"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
              </svg>
              Devices
            </ToggleButton>

            <ToggleButton
              $active={showDeviceLabels}
              onClick={() => setShowDeviceLabels(prev => !prev)}
              title="Show/Hide Labels"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 4v3h5.5v12h3V7H19V4H5z" />
              </svg>
              Labels
            </ToggleButton>

            <Legend>
              <LegendItem>
                <LegendIcon $color="#00d4ff" />
                Devices ({devices.length})
              </LegendItem>
            </Legend>
          </>
        )}

        {/* Кнопка полного экрана */}
        {imageUrl && (
          <ToggleButton $active={false} onClick={openFullscreen} title="Fullscreen">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
            </svg>
            Fullscreen
          </ToggleButton>
        )}
      </ControlsBar>

      {/* Fullscreen overlay через Portal */}
      {isFullscreen && imageUrl && ReactDOM.createPortal(
        <FullscreenOverlay>
          <FullscreenHeader>
            <FullscreenTitle>{mapData.name || 'Mission Map'}</FullscreenTitle>
            <CloseButton
              onClick={() => {
                setIsFullscreen(false);
                document.body.style.overflow = '';
              }}
              type="button"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Close (ESC)
            </CloseButton>
          </FullscreenHeader>
          <FullscreenContent>
            <FullscreenMapWrapper style={{ width: fullscreenSize.width, height: fullscreenSize.height }}>
              {/* Фоновое изображение */}
              <FallbackImageContainer>
                <FallbackImage src={imageUrl} alt="Mission Map Fullscreen" />
              </FallbackImageContainer>

              {/* Canvas для устройств */}
              {showDeviceIcons && devices.length > 0 && (
                <Stage
                  width={fullscreenSize.width}
                  height={fullscreenSize.height}
                  style={{ position: 'absolute', top: 0, left: 0 }}
                >
                  <Layer>
                    {devices.map((device, index) =>
                      renderDevice(device, index, fullscreenSize.width / CANVAS_WIDTH)
                    )}
                  </Layer>
                </Stage>
              )}
            </FullscreenMapWrapper>
          </FullscreenContent>
        </FullscreenOverlay>,
        document.body
      )}
    </ViewerContainer>
  );
};

export default MissionMapViewer;
